import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { AlertCircle, Calendar, Eye, Search, X, XCircle } from "lucide-react";

import "../../RessourcesStyles/TraitementDemandes.css";

import DemandeDetailsModal from "./DemandeDetailsModal";
import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import {
    cancelEventDemandeRequest,
    getMyFormationDemandesRequest,
} from "../../../../../redux/actions/demandesActions";

const isFormationCampaign = (campagne) => String(campagne?.typeCampagne || "").toUpperCase() === "FORMATION";

const fmt = (iso) => {
    if (!iso) return "-";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
    } catch {
        return String(iso);
    }
};

const normalizeStatus = (status) => String(status || "").toUpperCase();

const isPendingStatus = (status) =>
    ["PENDING", "PENDING_MANAGER", "PENDING_RF"].includes(normalizeStatus(status));

const getProcessedAt = (demande) => demande?.rfDecisionAt || demande?.managerDecisionAt || demande?.updatedAt || null;

const getStatusLabel = (status) => {
    const value = normalizeStatus(status);
    if (["APPROVED", "VALIDATED", "ACCEPTED", "RF_APPROVED", "MANAGER_APPROVED"].includes(value)) return "Validée";
    if (["REJECTED", "REFUSED", "RF_REJECTED", "MANAGER_REJECTED"].includes(value)) return "Refusée";
    if (["CANCELLED", "WITHDRAWN"].includes(value)) return "Annulée";
    if (["IN_PROGRESS", "ONGOING", "PENDING_RF"].includes(value)) return "En cours";
    if (["PENDING", "PENDING_MANAGER"].includes(value)) return "En attente";
    return status || "En attente";
};

const getStatusClass = (status) => {
    const label = getStatusLabel(status);
    if (label === "Validée") return "tdStatusBadge--success";
    if (label === "Refusée") return "tdStatusBadge--danger";
    if (label === "Annulée") return "tdStatusBadge--muted";
    if (label === "En cours") return "tdStatusBadge--info";
    return "tdStatusBadge--warning";
};

const getTrainingType = (demande) => {
    const raw = String(demande?.requestType || demande?.formationType || demande?.source || demande?.catalogueType || demande?.mode || "").toUpperCase();
    if (raw.includes("HORS")) return "Hors catalogue";
    if (demande?.formationId || demande?.catalogueId || demande?.catalogueNom || demande?.formationNom) return "Catalogue";
    return "Hors catalogue";
};

const getCurrentUserId = (state) =>
    state?.auth?.user?.id ||
    // state?.user?.currentUser?.id ||
    // state?.user?.user?.id ||
    sessionStorage.getItem("uid");

const matchesCurrentUser = (demande, currentUserId) => {
    if (!currentUserId) return true;
    const explicitIds = [
        demande?.targetUserId,
        demande?.userId,
        demande?.collaborateurId,
        demande?.demandeurId,
        demande?.createdById,
    ].filter((value) => value !== undefined && value !== null && value !== "");

    if (explicitIds.length === 0) return true;
    return explicitIds.some((value) => String(value) === String(currentUserId));
};

export default function MesDemandesCollaborateur() {
    const dispatch = useDispatch();
    const { loading, myFormationDemandes = [] } = useSelector((s) => s.demandes || {});
    const { visibleList: campagnesRaw = [], loading: campagnesLoading } = useSelector((s) => s.campagnes || {});
    const currentUserId = useSelector(getCurrentUserId);

    const [selectedDemande, setSelectedDemande] = useState(null);
    const [search, setSearch] = useState("");
    const [campagneId, setCampagneId] = useState("");

    const campagnes = useMemo(() => (campagnesRaw || []).filter(isFormationCampaign), [campagnesRaw]);

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(getMyFormationDemandesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (!campagneId && campagnes.length) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault || c?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne.id));
        }
    }, [campagnes, campagneId]);

    const demandes = useMemo(
        () =>
            (myFormationDemandes || []).filter((demande) => {
                const isFormation = String(demande?.type || "").toUpperCase() === "FORMATION";
                const inCampaign = !campagneId || String(demande?.campagneId || "") === String(campagneId);
                return isFormation && inCampaign && matchesCurrentUser(demande, currentUserId);
            }),
        [myFormationDemandes, campagneId, currentUserId]
    );

    const filteredDemandes = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return demandes;
        return demandes.filter((demande) => {
            const ref = String(demande?.reference || demande?.numero || demande?.id || "").toLowerCase();
            const formation = String(demande?.formationNom || demande?.eventTitre || demande?.titre || "").toLowerCase();
            const status = getStatusLabel(demande?.status).toLowerCase();
            return ref.includes(query) || formation.includes(query) || status.includes(query);
        });
    }, [demandes, search]);

    const handleCancel = async (demande) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "",
            text: "Voulez-vous vraiment annuler cette demande ?",
            showCancelButton: true,
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
            confirmButtonColor: "#dc3545",
        });

        if (!result.isConfirmed) return;

        dispatch(
            cancelEventDemandeRequest({
                demandeId: demande.id,
                motif: "Annulation par le collaborateur",
                comment: null,
            })
        );
    };

    return (
        <div className="my-training-requests-page tdPage tdPage--collabRequests">
            <div className="my-training-requests-header">
                <div>
                    <h1>Mes demandes</h1>
                </div>
            </div>

            <div className="my-training-requests-filters">
                    <div className="requests-filter-campaign">
                        <label>Campagne de formation</label>
                        <select
                            value={campagneId}
                            onChange={(event) => setCampagneId(event.target.value)}
                            disabled={campagnesLoading}
                        >
                            <option value="">Toutes les campagnes</option>
                            {campagnes.map((campagne) => (
                                <option key={campagne.id} value={campagne.id}>
                                    {campagne.nom || campagne.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="requests-filter-search">
                        <Search size={16} className="search-icon" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher par formation, reference ou statut"
                        />
                        {search && (
                            <button type="button" className="tdSearchClearBtn" onClick={() => setSearch("")} title="Effacer">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="my-training-requests-table-card tdCard tdCard--management">
                    {loading ? (
                        <div className="tdEmpty">Chargement...</div>
                    ) : filteredDemandes.length === 0 ? (
                        <div className="tdEmpty">
                            <AlertCircle size={20} />
                            Aucune demande de formation trouvée.
                        </div>
                    ) : (
                        <div className="tdTableWrap">
                            <table className="tdTable tdTable--collab">
                                <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Formation</th>
                                    <th>Type</th>
                                    <th>Campagne</th>
                                    <th>Date de demande</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredDemandes.map((demande) => (
                                    <tr key={demande.id}>
                                        <td>
                                            <div className="tdPrimary">{demande.reference || demande.numero || `DF-${demande.id}`}</div>
                                            <div className="tdSub">ID: {demande.id}</div>
                                        </td>
                                        <td className="tdTrunc" title={demande.formationNom || demande.eventTitre || ""}>
                                            {demande.formationNom || demande.eventTitre || "-"}
                                        </td>
                                        <td>
                                            <span className="tdTypeBadge">{getTrainingType(demande)}</span>
                                        </td>
                                        <td>{demande.campagneNom || "-"}</td>
                                        <td>
                                            <div className="tdDate">
                                                <Calendar size={16} />
                                                {fmt(demande.createdAt)}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`tdStatusBadge ${getStatusClass(demande.status)}`}>
                                                {getStatusLabel(demande.status)}
                                            </span>
                                            {getProcessedAt(demande) && (
                                                <div className="tdSub">Traité le {fmt(getProcessedAt(demande))}</div>
                                            )}
                                        </td>
                                        <td className="is-right">
                                            <div className="tdRowActions">
                                                <button
                                                    type="button"
                                                    className="tdIconBtn"
                                                    title="Voir detail"
                                                    onClick={() => setSelectedDemande(demande)}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                {isPendingStatus(demande.status) && (
                                                    <button
                                                        type="button"
                                                        className="tdIconBtn tdIconBtn--danger"
                                                        title="Annuler"
                                                        onClick={() => handleCancel(demande)}
                                                    >
                                                        <XCircle size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>

            <DemandeDetailsModal
                open={!!selectedDemande}
                demande={selectedDemande}
                onClose={() => setSelectedDemande(null)}
            />
        </div>
    );
}
